import { Button, Input, Select, Tooltip } from "antd";
import { TableProps } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import IoIosSendIcon from "../../../assets/icons/IoIosSendIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SentIcon from "../../../assets/icons/SentIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import RedirectProfile from "../../../components/common/RedirectProfile";
import TopCard from "../../../components/common/TopCard";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import {
  EmployeeData,
  GetEmployeeParamsType,
} from "../../../features/emp-setting/employee/employee.type";
import {
  useEmployeeReportMutation,
  useReInvitedMutation,
  useUpdateEmployeeBulkStatusMutation,
} from "../../../features/emp-setting/employee/employeeApi";
import {
  useGetProtalAllDepartmentQuery,
  useGetProtalAllSubDepartmentQuery,
} from "../../../features/portal/settings/settingsApi";
import { useGetAllUsersQuery } from "../../../features/portal/user/userApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
} from "../../../utils/utils";
import AddEmployeeModal from "./_components/AddEmployeeModal";
import BKashNumberModal from "./_components/BKashNumberModal";
import GrossSalaryModal from "./_components/GrossSalaryModal";
import InviteEmployeeModal from "./_components/InviteEmployeeModal";
import RequestDateWithOutDateModal from "./_components/RequestDateWithOutDateModal";
import VariableSalaryModal from "./_components/VariableSalaryModal";

enum Status {
  active = "status-success",
  inactive = "status-danger",
  invited = "status-info",
}

type ModalType =
  | "INVITE"
  | "GROSS_SALARY"
  | "VARIABLE_SALARY"
  | "BKASH_NUMBER"
  | "BULK_ACTION"
  | "SEND_INVITATION_AGAIN"
  | "START_ENROLLMENT"
  | "CREATE_DEVICE_USER"
  | "REPORT_REQUEST";

// const searchTypeSelect = ["name", "email", "employee_id", "mobile"]?.map(
//   (item) => ({
//     label: underscoreToCapitalizeString(item),
//     value: item,
//   })
// );

type GetEmployeeTypeParams = {
  attendanceType?: "remote" | "location" | "wifi";
} & GetEmployeeParamsType;

export default function Employees() {
  const location = useLocation();
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const { open, handleClose, handleOpen } = useModal<string, ModalType>();
  const { user } = useAppSelector((state) => state.auth);
  const { query } = useQueryParams();

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleFilter,
    filter,
    keyword,
    handleSearch,
  } = useTable<GetEmployeeTypeParams>({
    defaultFilter: {
      // search_type: "name",
      status: (query.status as GetEmployeeTypeParams["status"]) || "active",
      gender: (query.gender as GetEmployeeTypeParams["gender"]) || undefined,
      employee_type:
        (query.employee_type as GetEmployeeTypeParams["employee_type"]) ||
        undefined,
      dashboard:
        (query.dashboard as GetEmployeeTypeParams["dashboard"]) || undefined,
    },
  });
  const [
    updateEmployeeBulkStatus,
    {
      isLoading: isBulkStatusLoading,
      isError: isBulkStatusError,
      error: bulkStatusError,
      isSuccess: isBulkStatusSuccess,
    },
  ] = useUpdateEmployeeBulkStatusMutation();
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetProtalAllDepartmentQuery({
    params: {
      skip: 0,
      limit: 0,
      is_published: 1,
      company_id: user?.data?.company_id,
    },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetProtalAllSubDepartmentQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(filter?.department_id
          ? {
              business_department_id: filter?.department_id?.toString() || "",
            }
          : {}),
        status: true,
        company_id: user?.data?.company_id,
      },
    },
    {
      skip: !filter?.department_id,
    }
  );

  const employeeParams = {
    ...filter,
    search_type: undefined,
    ...(keyword ? { search_type: filter.search_type } : {}),
    search: keyword,
    is_location_active: filter?.attendanceType === "location" ? 1 : undefined,
    is_remote_active: filter?.attendanceType === "remote" ? 1 : undefined,
    is_wifi_network_active: filter?.attendanceType === "wifi" ? 1 : undefined,
    attendanceType: undefined,
    company_id: user?.data?.company_id,
    page,
    skip: undefined,
    // manager_id: authInfo?.role === "Manager" ? user?.data?.id : undefined,
  } as GetEmployeeTypeParams;

  const { isLoading, isError, error, data } = useGetAllUsersQuery({
    params: employeeParams,
  });
  const [
    reInvited,
    {
      isError: isReInviteError,
      error: reInviteError,
      isSuccess: isReInviteSuccess,
      isLoading: isReInviteLoading,
    },
  ] = useReInvitedMutation();
  // const {
  //   data: payrollSettingData,
  //   isError: isPayrollSettingError,
  //   error: payrollSettingError,
  // } = useGetPayrollSettingQuery();
  const [
    employeeReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useEmployeeReportMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isDesignationError, designationError, "Get designation fail");
  useError(isBulkStatusError, bulkStatusError, "Update bulk status fail");
  useError(isReInviteError, reInviteError, "Re-invitation mail sent fail");
  // useError(
  //   isPayrollSettingError,
  //   payrollSettingError,
  //   "Gross salary configured data fail "
  // );

  // const isShowModal =
  //   payrollSettingData?.data?.payroll_setting?.pay_components?.pay_schedule
  //     ?.is_enable;

  const handleUpdateStatus = () => {
    updateEmployeeBulkStatus({
      status: open?.data,
      employee_id: selectedData,
    });
  };

  const handleSandInvitationAgain = () => {
    const { id, ...rest } = JSON.parse(open?.data || "");

    reInvited({
      id,
      body: {
        ...rest,
      },
    });
  };

  useEffect(() => {
    if (isBulkStatusSuccess) {
      toast("success", "Statuses updated successfully");
      setSelectedData([]);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkStatusSuccess]);

  const columns: TableProps<EmployeeData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Employee Id",
      dataIndex: "employee_id",
      sorter: (a, b) =>
        Number(a?.employee_id ?? 0) - Number(b?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val, record) => (
        <span className="flex items-center gap-1.5">
          {/* {record?.payroll_show_alert && isShowModal ? (
            <Tooltip placement="top" title="Salary not configured!">
              <GoAlertFill
                style={{
                  color: colors?.danger?.color,
                  fontSize: 18,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          ) : null}{" "} */}
          {val ?? "n/a"}
        </span>
      ),
    },
    {
      title: "Employee Name",
      dataIndex: "name",
      sorter: (a, b) =>
        a?.name && b?.name ? a.name.localeCompare(String(b.name)) : 0,
      sortIcon: () => <SortIcon />,
      // // GS-4 Start
      render: (val, record) => (
        <RedirectProfile id={record?.id}>{val ?? "n/a"}</RedirectProfile>
      ),
      width: 200,
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobile",
      render: (val) => val ?? "n/a",
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (val) => val?.name ?? "n/a",
    },
    {
      title: "Designation",
      dataIndex: "sub_department",
      render: (val) => val?.name ?? "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: keyof typeof Status, record) => (
        <div className="flex items-center gap-2">
          <span className={Status[val]}>{capitalize(val || "-")}</span>

          {val === "invited" && (
            <Tooltip placement="top" title="Send invitation again">
              <button
                className="text-[#001652]"
                onClick={() =>
                  handleOpen({
                    type: "SEND_INVITATION_AGAIN",
                    data: JSON.stringify({
                      id: record?.id,
                      email: record?.member?.profile?.email,
                      profile_id: record?.member?.profile?.id,
                    }),
                  })
                }
              >
                <IoIosSendIcon />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
    // {
    //   title: "In Device",
    //   dataIndex: "is_in_attendance_device",
    //   render: (val) => (val === true ? "Yes" : val === false ? "No" : "n/a"),
    // },
    {
      title: "Action",
      dataIndex: "id",
      align: "center",

      render: (val, record) => (
        <div className="flex items-center justify-center gap-2">
          {/* <Button
            className={`action-button bg-[#ffffff] `}
            size="small"
            icon={<AssignDeviceIcon />}
            disabled={record?.is_in_attendance_device === false}
            onClick={() =>
              handleOpen({
                type: "START_ENROLLMENT",
                data: val,
              })
            }
          /> */}

          <Link to={`${location.pathname}/view/${val}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (isReInviteSuccess) {
      toast("success", "Re-invitation mail sent successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReInviteSuccess]);

  return (
    <>
      <SidebarLayout title="Employees List">
        <TopCard className="mb-4" leftChildren={<div />}>
          <Button
            icon={<AddIcon />}
            type="primary"
            className="sm-button !px-3"
            onClick={() =>
              handleOpen({
                type: "ADD",
              })
            }
          >
            Add
          </Button>
          {/* <Button
                icon={<AddIcon />}
                type="primary"
                className="sm-button !px-3"
                onClick={() =>
                  handleOpen({
                    type: "CREATE_DEVICE_USER",
                  })
                }
              >
                Create Device User
              </Button> */}
          <Button
            icon={<SentIcon />}
            type="primary"
            className="!bg-[#001652] sm-button !px-3"
            onClick={() =>
              handleOpen({
                type: "INVITE",
              })
            }
          >
            Invite
          </Button>
          {/* <Button
            className="upload-button"
            icon={<UploadIcon />}
            onClick={() =>
              handleOpen({
                type: "VARIABLE_SALARY",
              })
            }
          >
            Upload Variable Salary
          </Button>
          <Button
            className="upload-button"
            icon={<UploadIcon />}
            onClick={() =>
              handleOpen({
                type: "GROSS_SALARY",
              })
            }
          >
            Upload Gross Salary
          </Button>
          <Button
            className="bkash-button"
            icon={<UploadIcon />}
            onClick={() =>
              handleOpen({
                type: "BKASH_NUMBER",
              })
            }
          >
            Upload bKash A/C
          </Button> */}
          {/* <Button
            loading={excelLoading}
            onClick={handleDownload}
            className="download-button"
            icon={<DownloadIcon />}
          >
            Download
          </Button> */}
          {/* <Button
            onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
            className="download-button"
            icon={<DownloadIcon />}
          >
            Report Requests
          </Button> */}

          {/* <Button
            loading={excelLoading}
            onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
            className="download-button"
            icon={<DownloadIcon />}
          >
            Report Requests
          </Button> */}
          {/* <Button
          type="primary"
          className="middle-button"
          icon={<SettingIcon />}
        /> */}
        </TopCard>
        {/* 
        {isShowModal &&
        (filter.status === "active" || !filter.status) &&
        data?.data?.employees?.find(
          (item) => item?.status === "active" && item?.payroll_show_alert
        ) ? (
          <Alert
            message="One or more employees have not their salary configured. If not, then their salary will not be disbursed."
            type="warning"
            showIcon
            closable
            icon={<GoAlertFill style={{ color: "#fff", fontSize: 20 }} />}
            closeIcon={<CloseIcon width={10} height={10} />}
            className="mb-4 rounded bg-[#f59e0b] font-medium text-sm text-white !px-4 !py-2.5"
          />
        ) : null} */}

        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.total}
          dataSource={addKeyInArray(data?.data?.users)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          // rowClassName={(record) =>
          //   record?.payroll_show_alert && isShowModal ? "alert_bg" : ""
          // }
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedData,
            onChange: (key) => {
              setSelectedData(key as string[]);
            },
            getCheckboxProps: (record) => {
              return {
                disabled: record?.status === "invited",
              };
            },
            columnWidth: 40,
          }}
          filterSection={
            <>
              {selectedData?.length ? (
                <Select
                  showSearch
                  placeholder="Bulk Action"
                  // value={bulkAction}
                  options={bulkActionList}
                  onChange={(val) =>
                    handleOpen({
                      type: "BULK_ACTION",
                      data: val,
                    })
                  }
                  filterOption={selectSearchOption}
                  className="min-w-[100px]"
                  suffixIcon={<SelectDownIcon />}
                  rootClassName="icon-gray"
                />
              ) : null}
              {/* <Select
                showSearch
                placeholder="Search Type"
                value={filter.search_type}
                options={searchTypeSelect}
                onChange={(val) => handleFilter("search_type", val)}
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                popupMatchSelectWidth={false}
              /> */}
              <Input
                key={filter.search_type}
                suffix={<SearchIcon />}
                // placeholder={`Search by ${underscoreToCapitalizeString(
                //   filter.search_type || "",
                //   true
                // )}`}
                placeholder="Search by name, email and mobile number"
                style={{ width: 350 }}
                onChange={(val) => handleSearch(val)}
              />
              <Select
                allowClear
                showSearch
                placeholder="Department"
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  departmentData?.data?.departments || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter?.department_id}
                onChange={(val) => {
                  handleFilter("department_id", val);
                  handleFilter("sub_department_id", undefined);
                }}
                popupMatchSelectWidth={200}
              />
              <Select
                allowClear
                showSearch
                placeholder="Designation"
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  designationData?.data?.data || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter?.sub_department_id}
                onChange={(val) => handleFilter("sub_department_id", val)}
                disabled={!filter.department_id}
                popupMatchSelectWidth={200}
              />
              {/* <Select
                allowClear
                showSearch
                placeholder="Attendance Type"
                options={attendanceTypeList}
                onChange={(val) => handleFilter("attendanceType", val)}
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              /> */}
              {/* <Select
                allowClear
                showSearch
                placeholder="In Device"
                options={inDeviceList}
                onChange={(val) =>
                  handleFilter(
                    "is_in_attendance_device",
                    val === "true" ? true : val === "false" ? false : undefined
                  )
                }
                filterOption={selectSearchOption}
                className="min-w-[100px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              /> */}
              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(
                  ["active", "inactive", "invited"],
                  "string",
                  true
                )}
                className="min-w-[90px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter.status}
                onChange={(val) => handleFilter("status", val)}
              />
            </>
          }
        />

        {open.type === "ADD" && (
          <AddEmployeeModal
            open={open.open}
            handleClose={handleClose}
            // showGrossAmount={!!isShowModal}
          />
        )}
        {open.type === "INVITE" && (
          <InviteEmployeeModal open={open.open} handleClose={handleClose} />
        )}
        {open.type === "GROSS_SALARY" && (
          <GrossSalaryModal open={open.open} handleClose={handleClose} />
        )}
        {open.type === "VARIABLE_SALARY" && (
          <VariableSalaryModal open={open.open} handleClose={handleClose} />
        )}
        {open.type === "BKASH_NUMBER" && (
          <BKashNumberModal
            open={open.open}
            handleClose={handleClose}
            employeeParams={filter}
          />
        )}
        {open.type === "BULK_ACTION" && (
          <ConfirmModal
            open={open?.open}
            handleClose={handleClose}
            handleOk={handleUpdateStatus}
            confirmTitle={`${capitalize(open.data || "Update Status")} Users`}
            questionText={`Are you sure to ${
              open?.data || "update status"
            } this users?`}
            okText="Confirm"
            okLoading={isBulkStatusLoading}
          />
        )}

        {open?.type === "SEND_INVITATION_AGAIN" && (
          <ConfirmModal
            open={open.open}
            handleClose={handleClose}
            handleOk={handleSandInvitationAgain}
            confirmTitle="Confirmation"
            questionText="Are you sure you want send invitation mail again?"
            okText="Send"
            okLoading={isReInviteLoading}
          />
        )}

        {/* {open.type === "START_ENROLLMENT" && (
          <StartEnrollmentModal
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
          />
        )} */}

        {/* {open.type === "CREATE_DEVICE_USER" && (
          <CreateDeviceUserModal open={open.open} handleClose={handleClose} />
        )} */}

        {open.type === "REPORT_REQUEST" && (
          <RequestDateWithOutDateModal
            open={open.open}
            handleClose={handleClose}
            isLoading={isGenerateLoading}
            isSuccess={isGenerateSuccess}
            // detailsMessage={`You are requesting a Report Export range for ${dayjs(
            //   filter?.date,
            //   dbDateFormat
            // ).format("DD/MM/YYYY")}`}
            handleSubmit={() => {
              employeeReport({
                body: {
                  name: "Employee List",
                },
                params: { ...filter, skip: 0, limit: 0 },
              });
            }}
          />
        )}

        {/* {open.type === "REPORT_REQUEST" && (
          <ReportRequestModal open={open.open} handleClose={handleClose} />
        )} */}
      </SidebarLayout>
    </>
  );
}

const bulkActionList = [
  {
    label: "Active",
    value: "active",
  },
  {
    label: "Inactive",
    value: "inactive",
  },
];

export const attendanceTypeList = [
  {
    label: "Remote",
    value: "remote",
  },
  {
    label: "Location",
    value: "location",
  },
  {
    label: "Wifi",
    value: "wifi",
  },
];

const inDeviceList = [
  {
    label: "Yes",
    value: "true",
  },
  {
    label: "No",
    value: "false",
  },
];
